import { useState, useCallback } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import useSharedShowPageContext from '../../common/hooks/useSharedShowPageContext';
import ChannelEpisodes from '../../common/components/ChannelEpisodes';
import useEpisodes from '../../common/hooks/useEpisodes';
import useShow from '../../../../../hooks/shows/useShowFromURL';
import useShowUrlParams from '../../../../../hooks/shows/useShowUrlParams';
import styles from './styles.module.scss';
import { size } from '@/helpers/lodash';
import { selectAudioPaused } from '@/state/selectors/audio';
import Gradient, {
  GRADIENT_POSITION_BOTTOM,
  GRADIENT_POSITION_TOP,
  GRADIENT_WEIGHT_KEY_LARGE
} from '@/components/app/Gradient';
import { classes } from '@/helpers/styling';
import DropDownSelect from '@/components/component-library/DropDownSelect';
import ErrorWidget from '@/components/app/Error/ErrorWidget';
import Text, { FONT_SIZE_090, FONT_SIZE_070 } from '@/components/component-library/Text';
import { BLACK_MATTE } from '@/config/colors';


const selector = (state) => ({ isPaused: selectAudioPaused(state) });

const EpisodesSection = () => {
  const {
    episodeNumber: scrollToEpisodeNumber,
    seasonNumber: scrollToSeasonNumber
  } = useShowUrlParams();
  const {
    shouldScrollToEpisode,
    onScrolledToEpisode,
    haveSetSeasonFromUrl,
    onClickPlayEpisode,
    episodeScrollContainerRef,
    currentSeasonNumber,
    seasonOptions,
    onChangeSeason
  } = useSharedShowPageContext();
  const { isPaused } = useSelector(selector, shallowEqual);

  const {
    isError: episodesError,
    refetch: episodesRetry,
    isLoading: episodesLoading,
    isFetching: episodesFetching,
    episodes: episodePreviews
  } = useEpisodes();

  const { isLoading } = useShow();

  const [seasonsDropOpen, setSeasonsDropOpen] = useState(false);

  const handleCloseSeasons = useCallback(() => setSeasonsDropOpen(false), []);

  const handleOpenSeasons = useCallback(() => setSeasonsDropOpen(true), []);

  const useSeasonsDropDown = size(seasonOptions) > 1;

  return (
    <div className={styles.episodes}>
      <div className={styles.episodesHeader}>
        <div className={styles.episodesTitle}>
          <Text
            bold
            className={styles.episodesTitleText}
            loading={isLoading}
            size={FONT_SIZE_090}
            useHeaderFont
          >
            Episodes
          </Text>
        </div>
        <div
          className={classes(
            styles.seasonsDropDownWrapper,
            { [styles.disableDropDown]: !useSeasonsDropDown },
            isLoading || !haveSetSeasonFromUrl ? '--is-transitioning' : '--is-transitioned'
          )}
        >
          {useSeasonsDropDown ? (
            <DropDownSelect
              disableArrow={size(seasonOptions) < 2}
              disabled={size(seasonOptions) < 2}
              isSelectable
              loading={!seasonOptions.length}
              onClickOption={({ id }) => onChangeSeason(id)}
              onClose={handleCloseSeasons}
              onOpen={handleOpenSeasons}
              open={seasonsDropOpen}
              options={seasonOptions}
              placeholder={`Season ${scrollToSeasonNumber}`}
              selectedId={currentSeasonNumber}
            />
          ) : (
            <Text bold fixed size={FONT_SIZE_070} useHeaderFont>
              {(seasonOptions.find(({ id }) => id === currentSeasonNumber) || {}).value}
            </Text>
          )}
        </div>
      </div>
      {!episodesError ? (
        <div
          className={styles.episodesContent}
          ref={episodeScrollContainerRef}
        >
          <Gradient
            className={styles.episodesScrollGradientTop}
            colour={BLACK_MATTE}
            gradientBreadth='40px'
            position={GRADIENT_POSITION_TOP}
            weight={GRADIENT_WEIGHT_KEY_LARGE}
          />
          <div className={classes(styles.episodesScroll, { [styles.isLoading]: episodesLoading })}>
            <ChannelEpisodes
              episodePreviews={episodePreviews}
              haveSetSeasonFromUrl={haveSetSeasonFromUrl}
              isPaused={isPaused}
              loading={episodesLoading}
              loadingCount={4}
              onClickPlayFromChannel={onClickPlayEpisode}
              onScrolledToEpisode={onScrolledToEpisode}
              scrollingRef={episodeScrollContainerRef}
              scrollToEpisodeNumber={scrollToEpisodeNumber}
              scrollToSeasonNumber={scrollToSeasonNumber}
              seasonId={currentSeasonNumber}
              shouldScrollToEpisode={shouldScrollToEpisode}
            />
          </div>
          <Gradient
            className={styles.episodesScrollGradientBottom}
            colour={BLACK_MATTE}
            directionReverse
            extraBreadth={false}
            gradientBreadth='120px'
            position={GRADIENT_POSITION_BOTTOM}
            weight={GRADIENT_WEIGHT_KEY_LARGE}
          />
        </div>
      ) : (
        <ErrorWidget
          errorMessage='Failed to fetch episodes'
          onRetry={episodesRetry}
          shouldDisplay={episodesError && !episodesFetching}
        />
      )}
    </div>
  );
};

export default EpisodesSection;
